import { useState } from "react";
import { Download, Loader2, MoreVertical } from "lucide-react";
import { toast } from "sonner";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@bookdental/ui";
import type { PlanilhaDoRegistro } from "@bookdental/schemas";
import { cn } from "@/lib/cn";
import { trpc } from "@/lib/trpc";
import { exportarTabela, type ColunaExport, type FormatoExport } from "@/lib/exportar-tabela";
import { ExportarDialog } from "./exportar/ExportarDialog";

type Props<T> = {
  nome: string;
  /** No título do diálogo: "Exportar pacientes". */
  titulo?: string;
  /** Onde a escolha de colunas fica lembrada. Padrão: `nome` — passe fixo quando o nome muda com
   *  o filtro ("lancamentos-2026-10"), senão cada mês começaria do zero. */
  tela?: string;
  /** Quantas linhas vão sair, quando a tela já sabe — aparece no diálogo. */
  quantidade?: number;
  /** A tela tem importação: o diálogo oferece a coluna ID, que faz a planilha voltar atualizando. */
  reimportavel?: boolean;
  /**
   * Qual planilha é, pro servidor autorizar e registrar antes do arquivo existir (ver `PLANILHAS`).
   * Sem ela, quem autoriza é o próprio `buscarLinhas` — o caso de pacientes, com endpoint próprio.
   */
  planilha?: PlanilhaDoRegistro;
  /**
   * As colunas do arquivo. Pode ser função pra ser resolvida no clique, e não no render — é o que a
   * planilha de produção precisa: a ordem e o que está escondido vivem no localStorage, que não é
   * reativo, então uma lista calculada no render nasce velha assim que alguém mexe nas colunas.
   */
  colunas: ColunaExport<T>[] | (() => ColunaExport<T>[]);
  idDe: (linha: T) => string;
  /**
   * Busca TODAS as linhas que devem sair — não as que estão carregadas na tela.
   *
   * Existe como função assíncrona por causa da paginação: as listas trazem 50-200 por vez, e
   * exportar o que está em memória entregaria "800 pacientes" viraram 50 sem ninguém perceber. Quem
   * chama pagina até o fim aqui dentro. Só roda no "Baixar": abrir o diálogo não busca nada.
   * Recebe as colunas marcadas, pra quem pede ao servidor só os campos delas.
   */
  buscarLinhas: (colunas: ColunaExport<T>[]) => Promise<T[]>;
  rotulo?: string;
  className?: string;
  disabled?: boolean;
  /** `tresPontos`: só o ícone ⋮ ("Mais opções"), pra telas apertadas como a lista de pacientes no
   *  celular — o menu abre com "Exportar". */
  gatilho?: "botao" | "tresPontos";
};

/** Exportar a tabela como Excel ou CSV, escolhendo colunas e ordem antes. */
export function BotaoExportar<T>({
  nome,
  titulo = "planilha",
  tela = nome,
  quantidade,
  reimportavel = false,
  planilha,
  colunas,
  idDe,
  buscarLinhas,
  rotulo = "Exportar",
  className,
  disabled,
  gatilho = "botao",
}: Props<T>) {
  const [ocupado, setOcupado] = useState(false);
  const [aberto, setAberto] = useState<ColunaExport<T>[] | null>(null);
  const abrir = () => setAberto(typeof colunas === "function" ? colunas() : colunas);
  const registrar = trpc.planilha.registrarExportacao.useMutation();

  async function exportar(escolha: { colunas: ColunaExport<T>[]; formato: FormatoExport; incluirId: boolean }) {
    setOcupado(true);
    try {
      const linhas = await buscarLinhas(escolha.colunas);
      if (linhas.length === 0) {
        toast.info("Nada pra exportar com os filtros atuais.");
        return;
      }
      // Sem a autorização do servidor, o arquivo não nasce (a recusa cai no catch abaixo).
      if (planilha) await registrar.mutateAsync({ planilha, linhas: linhas.length, colunas: escolha.colunas.map((c) => c.titulo) });
      const r = exportarTabela({ nome, linhas, idDe, ...escolha });
      toast.success(`${r.total} ${r.total === 1 ? "linha exportada" : "linhas exportadas"} em ${r.arquivo}`);
      setAberto(null);
    } catch (e: any) {
      toast.error(e?.message || "Não foi possível exportar.");
    } finally {
      setOcupado(false);
    }
  }

  return (
    <>
      {gatilho === "tresPontos" ? (
        <DropdownMenu modal={false}>
          <DropdownMenuTrigger asChild disabled={disabled || ocupado}>
            <button
              type="button"
              aria-label="Mais opções"
              title="Mais opções"
              className={cn(
                "inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-md border border-zinc-200 bg-white text-zinc-600 transition-colors hover:bg-zinc-50 disabled:opacity-60 dark:border-border dark:bg-background dark:text-foreground dark:hover:bg-muted",
                className,
              )}
            >
              {ocupado ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreVertical className="h-5 w-5" />}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-48 border border-zinc-200 bg-white shadow-lg dark:border-border dark:bg-card">
            <DropdownMenuItem onSelect={abrir} className="gap-2">
              <Download className="h-4 w-4" />
              {rotulo}
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      ) : (
        <button
          type="button"
          onClick={abrir}
          disabled={disabled || ocupado}
          className={cn(
            "inline-flex h-9 items-center gap-1.5 rounded-md border border-zinc-200 px-3 text-[13px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 disabled:opacity-60 dark:border-border dark:text-foreground dark:hover:bg-muted",
            className,
          )}
        >
          {ocupado ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          {rotulo}
        </button>
      )}
      {aberto && (
        <ExportarDialog
          key={tela}
          aberto
          onFechar={() => setAberto(null)}
          tela={tela}
          titulo={titulo}
          quantidade={quantidade}
          colunas={aberto}
          reimportavel={reimportavel}
          ocupado={ocupado}
          onExportar={exportar}
        />
      )}
    </>
  );
}

/**
 * Percorre uma lista paginada até o fim.
 *
 * `total` vem da própria resposta, então o laço para quando acabou de verdade — e o teto de páginas
 * é rede de segurança contra um `total` que não bate com o que a query devolve, pra não girar pra
 * sempre na cara do usuário.
 */
export async function buscarTudoPaginado<T>(
  pagina: (skip: number, take: number) => Promise<{ itens: T[]; total: number }>,
  take = 200,
): Promise<T[]> {
  const acumulado: T[] = [];
  let skip = 0;
  for (let i = 0; i < 100; i++) {
    const { itens, total } = await pagina(skip, take);
    acumulado.push(...itens);
    skip += itens.length;
    if (itens.length === 0 || acumulado.length >= total) break;
  }
  return acumulado;
}
